import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { getStatusTypeObject } from '@/Components/posts/status';
import { PostStatusEnum } from '@/graphql/types';
import React, { useMemo } from 'react';
import { z } from 'zod';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';
interface StatusType {
  icon?: React.ReactNode;
  label?: string;
  value: PostStatusEnum;
}

const visibilitySearchSchema = z.object({
  visibility: z.array(z.nativeEnum(PostStatusEnum)).nullish(),
});

const visibilityComponentProps = z.object({
  displayName: z.string().optional(),
  showHiddenBS: z.boolean().optional(),
  hidePrivate: z.boolean().optional(),
  hidePending: z.boolean().optional(),
  hideDraft: z.boolean().optional(),
  hideTrash: z.boolean().optional(),
});

type VisibilityFilterProps = z.infer<typeof visibilityComponentProps>;

const VisibilityFilter = ({
  displayName = 'Visibility',
  showHiddenBS = false,
  hidePrivate = false,
  hidePending = false,
  hideDraft = false,
  hideTrash = false,
}: VisibilityFilterProps) => {
  const { fastFilters, slowFilters, updateFilter, isUpdating } = __useFilterState(visibilitySearchSchema);

  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    const statusArray: Array<StatusType | null> = [
      getStatusTypeObject({ status: PostStatusEnum.Publish }) as StatusType,
      showHiddenBS ? (getStatusTypeObject({ status: PostStatusEnum.HiddenBS }) as StatusType) : null,
      hidePrivate ? null : (getStatusTypeObject({ status: PostStatusEnum.Hidden }) as StatusType),
      hideDraft ? null : (getStatusTypeObject({ status: PostStatusEnum.Draft }) as StatusType),
      hidePending ? null : (getStatusTypeObject({ status: PostStatusEnum.Pending }) as StatusType),
      hideTrash ? null : (getStatusTypeObject({ status: PostStatusEnum.Trash }) as StatusType),
    ];

    return statusArray
      .filter((status): status is StatusType => status !== null)
      .map(({ icon, label, value }) => ({
        name: label || value.toString(),
        icon: icon as React.ReactElement | undefined,
        value,
        onClick: () => {
          updateFilter((prev) => {
            let newVisibility = [...(prev?.visibility || []), value];
            if (prev?.visibility?.includes?.(value)) {
              newVisibility = prev.visibility.filter((status) => status !== value);
            }
            return {
              ...prev,
              visibility: newVisibility.length ? newVisibility : undefined,
            };
          });
        },
      }));
  }, [showHiddenBS, hidePrivate, hideDraft, hidePending, hideTrash]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className="z-btn-6"
        name={displayName}
        menuList={menuItems}
        type="multiple-select"
        options={{
          isSearchable: true,
          filterBar: slowFilters,
          filterBarKey: 'visibility',
        }}
      />
      <FilterBadge count={fastFilters?.visibility?.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const VisibilityFilterMemo = React.memo(VisibilityFilter);

export const FilterConfig_Visibility = {
  visibility: {
    searchSchema: visibilitySearchSchema,
    component: VisibilityFilterMemo,
    componentProps: visibilityComponentProps,
  },
} as const;
